import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import ProjectFilter from './components/ProjectFilter';
import ProjectModal from './components/ProjectModal';
import ProjectStats from './components/ProjectStats';
import { projects } from '../../data/projects';

const ProjectShowcase = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    technology: [],
    type: [],
    industry: []
  });
  const [sortBy, setSortBy] = useState('recent');

  // Extract unique filter options from projects
  const filterOptions = useMemo(() => {
    const technologies = [...new Set(projects.flatMap(p => p.technologies))].sort();
    const types = [...new Set(projects.map(p => p.type))].sort();
    const industries = [...new Set(projects.map(p => p.industry))].sort();
    
    return { technologies, types, industries };
  }, []);

  // Filter projects based on active filters
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];

    // Apply technology filter
    if (activeFilters.technology.length > 0) {
      filtered = filtered.filter(project =>
        activeFilters.technology.some(tech =>
          project.technologies.includes(tech)
        )
      );
    }

    // Apply type filter
    if (activeFilters.type.length > 0) {
      filtered = filtered.filter(project =>
        activeFilters.type.includes(project.type)
      );
    }

    // Apply industry filter
    if (activeFilters.industry.length > 0) {
      filtered = filtered.filter(project =>
        activeFilters.industry.includes(project.industry)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        return filtered.sort((a, b) => parseInt(b.year) - parseInt(a.year));
      case 'alphabetical':
        return filtered.sort((a, b) => a.title.localeCompare(b.title));
      case 'technology':
        return filtered.sort((a, b) => b.technologies.length - a.technologies.length);
      default:
        return filtered;
    }
  }, [activeFilters, sortBy]);

  const handleFilterChange = (category, values) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: values
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      technology: [],
      type: [],
      industry: []
    });
  };

  const handleProjectExpand = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const sortOptions = [
    { value: 'recent', label: 'Most Recent', icon: 'Calendar' },
    { value: 'alphabetical', label: 'A-Z', icon: 'ArrowUpDown' },
    { value: 'technology', label: 'Tech Stack', icon: 'Code2' }
  ];

  return (
    <>
      <Helmet>
        <title>Project Showcase - Technical Case Studies | Arvin Z's Portfolio</title>
        <meta name="description" content="Explore detailed technical case studies showcasing problem-solving methodology, architecture decisions, and development insights across various industries and technologies." />
        <meta name="keywords" content="project portfolio, case studies, technical projects, web development, software engineering, React, Node.js, Python" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Icon name="FolderOpen" size={16} />
                <span>Project Portfolio</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-6">
                Technical Case Studies
              </h1>
              
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Dive deep into my development journey through detailed project breakdowns, 
                technical challenges, solution architectures, and the lessons learned along the way.
              </p>
            </div>

            {/* Project Statistics */}
            <ProjectStats projects={projects} />
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Filters */}
            <ProjectFilter
              filters={filterOptions}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              projectCount={filteredProjects.length}
            />

            {/* Sort Controls */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <div className="flex space-x-2">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`flex items-center space-x-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all duration-200 ${
                        sortBy === option.value
                          ? 'bg-accent text-accent-foreground border-accent'
                          : 'bg-background text-text-secondary border-border hover:border-accent/50'
                      }`}
                    >
                      <Icon name={option.icon} size={14} />
                      <span className="hidden sm:inline">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                {filteredProjects.length} of {projects.length} projects
              </div>
            </div>

            {/* Projects Grid */}
            {filteredProjects.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onExpand={handleProjectExpand}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2">No Projects Found</h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your filters to see more projects.
                </p>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  iconName="RotateCcw"
                  iconPosition="left"
                  iconSize={16}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 px-6 lg:px-8 bg-muted">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary mb-4">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-xl text-text-secondary mb-8">
              Let's discuss your project and create something extraordinary together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={20}
                onClick={() => navigate('/contact-opportunities')}
                className="bg-accent hover:bg-hover text-accent-foreground"
              >
                Start a Conversation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Eye"
                iconPosition="left"
                iconSize={20}
                onClick={() => navigate('/code-laboratory-interactive-playground')}
              >
                Explore Code Lab
              </Button>
            </div>
          </div>
        </section>

        {/* Project Detail Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleModalClose}
        />
      </div>
    </>
  );
};

export default ProjectShowcase;
